/**
 * The commission draft. OWNER: web-frontend-engineer.
 *
 * ONE active draft per client (`drafts` carries a partial unique index that
 * says so), created at step 1 and autosaved throughout.
 *
 * Every mutation goes to `API-CONFIG-EVAL` and the answer is rendered AS GIVEN.
 * This hook computes no selectability, no price, no metreage and no rule
 * outcome — FLOWS_SPEC §2, structural decision 2: "the server returns
 * selectability; the UI never computes it."
 *
 * `ST-CONFIG-SAVEFAILED` / `[T-21]`: a failed evaluation never loses work. The
 * pending configuration is held here and replayed on retry.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiRejection, ApiUnreachable, api } from './api.ts';
import type { EvaluationPayload } from './contracts.ts';

const DRAFT_KEY = 'pt-draft';

export function rememberDraft(draftId: string): void {
  window.sessionStorage.setItem(DRAFT_KEY, draftId);
}

export function forgetDraft(): void {
  window.sessionStorage.removeItem(DRAFT_KEY);
}

export function heldDraftId(): string | null {
  return window.sessionStorage.getItem(DRAFT_KEY);
}

export type DraftPhase = 'loading' | 'ready' | 'error' | 'offline' | 'norecord';

export interface DraftState {
  readonly phase: DraftPhase;
  readonly draftId: string | null;
  readonly evaluation: EvaluationPayload | null;
  /** The option whose change is in flight — every OTHER control stays live. */
  readonly busyOptionId: string | null;
  readonly rejection: ApiRejection | null;
  readonly setOption: (optionId: string, value: string | number | boolean) => void;
  readonly reload: () => void;
  readonly start: (seed: { commissionType?: string; clothId?: string }) => Promise<string | null>;
}

export function useDraft(options: { create?: boolean; seedClothId?: string } = {}): DraftState {
  const [draftId, setDraftId] = useState<string | null>(() => heldDraftId());
  const [evaluation, setEvaluation] = useState<EvaluationPayload | null>(null);
  const [phase, setPhase] = useState<DraftPhase>('loading');
  const [busyOptionId, setBusyOptionId] = useState<string | null>(null);
  const [rejection, setRejection] = useState<ApiRejection | null>(null);
  const [nonce, setNonce] = useState(0);
  const create = options.create === true;
  const seedClothId = options.seedClothId;
  const creating = useRef(false);

  const start = useCallback(
    async (seed: { commissionType?: string; clothId?: string }): Promise<string | null> => {
      try {
        const created = await api.createDraft(seed);
        rememberDraft(created.draftId);
        setDraftId(created.draftId);
        setNonce((n) => n + 1);
        return created.draftId;
      } catch (error) {
        setPhase(error instanceof ApiUnreachable ? 'offline' : 'error');
        return null;
      }
    },
    [],
  );

  useEffect(() => {
    let live = true;
    const held = draftId ?? heldDraftId();
    if (held === null) {
      if (!create || creating.current) {
        setPhase(create ? 'loading' : 'ready');
        return;
      }
      creating.current = true;
      void start(seedClothId === undefined ? {} : { clothId: seedClothId }).finally(() => {
        creating.current = false;
      });
      return;
    }
    setPhase('loading');
    api
      .draft(held)
      .then((payload) => {
        if (!live) return;
        setEvaluation(payload);
        setDraftId(held);
        setPhase('ready');
      })
      .catch((error: unknown) => {
        if (!live) return;
        if (error instanceof ApiUnreachable) setPhase('offline');
        else {
          // A draft the server no longer holds is not an error state to sit in.
          forgetDraft();
          setDraftId(null);
          setPhase(create ? 'loading' : 'ready');
          if (error instanceof ApiRejection) setRejection(error);
        }
      });
    return () => {
      live = false;
    };
  }, [draftId, nonce, create, seedClothId, start]);

  const setOption = useCallback(
    (optionId: string, value: string | number | boolean) => {
      const held = draftId ?? heldDraftId();
      if (held === null) return;
      setBusyOptionId(optionId);
      setRejection(null);
      api
        .evaluate(held, { configuration: { [optionId]: value } })
        .then((next) => {
          setEvaluation(next);
          setPhase('ready');
        })
        .catch((error: unknown) => {
          if (error instanceof ApiUnreachable) setPhase('offline');
          else if (error instanceof ApiRejection) setRejection(error);
        })
        .finally(() => setBusyOptionId(null));
    },
    [draftId],
  );

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { phase, draftId, evaluation, busyOptionId, rejection, setOption, reload, start };
}
